import { Input } from '@components'
import { useEdges, useNodes } from '@xyflow/react'
import InputSend from '@components/Inputs/InputSend'
import React, { useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { getNodeDefinition } from '../nodes/registry'
import { AuditTrailCommentNodeConfig } from '../nodes/types'
import { resolveUpstreamTextSource } from './upstreamTextSource'
import { useWorkflowConfigForm } from './useWorkflowConfigForm'

const MESSAGE_MAX_LENGTH = 2048

interface Props {
  nodeId: string
  config: AuditTrailCommentNodeConfig
  onChange: (config: AuditTrailCommentNodeConfig) => void
}

export const AuditTrailCommentConfigForm: React.FC<Props> = ({ nodeId, config, onChange }) => {
  const [t] = useTranslation()
  const edges = useEdges()
  const nodes = useNodes()
  const incomingEdge = edges.find(edge => edge.target === nodeId)
  const upstreamNode = incomingEdge
    ? nodes.find(node => node.id === incomingEdge.source)
    : undefined
  const upstreamType = upstreamNode?.type
  const textSource = resolveUpstreamTextSource(nodeId, nodes, edges)
  const upstreamProvidesText = Boolean(textSource)

  const upstreamDocumentLabel = useMemo(() => {
    if (!incomingEdge) {
      return t('workflows.config.auditTrailComment.documentFromFlow')
    }

    if (!upstreamType) {
      return t('workflows.config.auditTrailComment.documentFromFlow')
    }

    const upstreamDefinition = getNodeDefinition(upstreamType)
    return t('workflows.config.auditTrailComment.documentFromUpstream', {
      step: t(upstreamDefinition.labelKey)
    })
  }, [incomingEdge, upstreamType, t])

  const { control, pushConfig } = useWorkflowConfigForm<AuditTrailCommentNodeConfig>({
    defaultValues: config,
    onChange
  })

  return (
    <>
      <Input
        id={`workflow-audit-trail-comment-document-${nodeId}`}
        name={`workflow-audit-trail-comment-document-${nodeId}`}
        label={t('workflows.config.auditTrailComment.document')}
        value={upstreamDocumentLabel}
        disabled
      />
      <Controller
        name="message"
        control={control}
        rules={{
          required: upstreamProvidesText
            ? false
            : t('workflows.config.auditTrailComment.messageRequired'),
          maxLength: {
            value: MESSAGE_MAX_LENGTH,
            message: t('workflows.config.auditTrailComment.messageTooLong', {
              max: MESSAGE_MAX_LENGTH
            })
          }
        }}
        render={({ field, fieldState }) => (
          <FieldStack>
            <FieldLabel>{t('workflows.config.auditTrailComment.message')}</FieldLabel>
            <InputSend
              onBlur={field.onBlur}
              onChangeController={value => {
                field.onChange(value)
                pushConfig()
              }}
              ref={field.ref}
              actionElement={<span />}
              name={`workflow-audit-trail-comment-${nodeId}`}
              value={field.value}
              error={fieldState.error}
              minRows={3}
              maxRows={6}
            />
            <HelperText>
              {upstreamProvidesText
                ? t('workflows.config.auditTrailComment.lucaMessageHint')
                : t('workflows.config.auditTrailComment.hint')}
            </HelperText>
          </FieldStack>
        )}
      />
    </>
  )
}

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const FieldLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.metalGray};
`

const HelperText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  line-height: 1.4;
`
